import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowTopRightOnSquareIcon, EyeIcon } from '@heroicons/react/24/outline';
import API from '../services/api';
import Modal from './Modal';
import { hasFirstHand } from '../utils/dataBasis';
import { formatCaseDate } from '../utils/formatDateValue';
import { getViolationColor, getViolationTypeLabel } from '../types/caseForm';
import { enumLabel } from '../utils/enumLabel';

/**
 * "Most similar cases": the five live cases in the same country whose embeddings sit nearest this
 * one's (GET /api/cases/:id/similar). It makes no claim that anything is related — it lists what the
 * engine already ranks closest so the reviewer can check. Each row shows the content similarity as
 * a percentage and a Preview button that opens a small popup (description, violation types,
 * incident date, Indigenous group(s)) so the reviewer can judge without leaving the page. Cases
 * already paired with this one by a dedup match are left out: pending ones are on the checker above,
 * dismissed ones were judged, confirmed ones are archived.
 *
 * Rendered inside the Case Duplicate Checker card (DeduplicationPanel), not as a card of its own.
 * Renders nothing when the backend flag is off (the route answers 404).
 */

export interface SimilarItem {
  id: string;
  caseNumber: string;
  incidentDate: string | null;
  place: string | null;
  country: string;
  violationTypes: string[];
  otherViolationTypes?: string[];
  status: string;
  isPrimaryData: boolean;
  dataBasis?: string;
  /** Cosine similarity of the two embeddings, 0..1. */
  similarity: number;
  description: string;
  /** The case's Indigenous group first, then the victims' groups that differ from it. */
  indigenousGroups: string[];
}

interface Props {
  caseId: string;
  /** Changes when the case is edited, so the list refetches (the embedding is regenerated then). */
  refreshKey?: string;
}

const pct = (similarity: number) => `${Math.round(similarity * 100)}%`;

const SimilarCasesList: React.FC<Props> = ({ caseId, refreshKey }) => {
  const q = useQuery<{ items: SimilarItem[]; reason?: string }>({
    queryKey: ['similar-cases', caseId, refreshKey ?? ''],
    queryFn: async () => (await API.get(`/api/cases/${caseId}/similar`)).data,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const [preview, setPreview] = useState<SimilarItem | null>(null);
  const closePreview = useCallback(() => setPreview(null), []);

  if (q.isError) return null;

  const items = q.data?.items ?? [];

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Most similar cases</h4>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Same country, ranked by content similarity. These may or may not be related — check them.</p>

      {q.isLoading ? (
        <div className="mt-3 text-sm text-gray-400 dark:text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          {q.data?.reason === 'no_embedding'
            ? 'No comparison yet: this case is indexed for similarity when the duplicate check runs on it.'
            : 'No comparable live cases in the same country.'}
        </div>
      ) : (
        <ul className="mt-2 divide-y divide-gray-100 dark:divide-gray-700">
          {items.map((c) => (
              <li key={c.id} className="py-2 text-sm flex items-center gap-3 min-w-0">
                {/* One line per row: the case, the similarity, the preview. Everything else is in the
                    preview popup, so the list reads as a list. */}
                <Link to={`/cases/${c.id}`} className="font-medium text-primary hover:underline whitespace-nowrap">{c.caseNumber}</Link>
                {hasFirstHand(c) && (
                  <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 ring-1 ring-gray-300 dark:ring-gray-600" title="Primary data">P</span>
                )}
                <span className="ml-auto font-semibold tabular-nums text-gray-700 dark:text-gray-200 whitespace-nowrap" title="Content similarity between the two cases, from their embeddings. Not a duplicate verdict.">
                  {pct(c.similarity)}
                </span>
                <button
                  type="button"
                  onClick={() => setPreview(c)}
                  title={`Preview ${c.caseNumber}`}
                  className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 dark:text-blue-300 hover:underline whitespace-nowrap"
                >
                  <EyeIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  Preview
                </button>
              </li>
          ))}
        </ul>
      )}

      {preview && <SimilarCasePreview item={preview} onClose={closePreview} />}
    </div>
  );
};

/** The little preview popup: enough of the other case to judge whether it is the same incident. */
const SimilarCasePreview: React.FC<{ item: SimilarItem; onClose: () => void }> = ({ item, onClose }) => {
  const coded = item.violationTypes.filter((t) => t !== 'OTHER' || (item.otherViolationTypes ?? []).length === 0);
  const other = item.otherViolationTypes ?? [];
  const dt = 'text-xs font-medium text-gray-500 dark:text-gray-400';
  const dd = 'mt-0.5 text-sm text-gray-900 dark:text-gray-100';
  return (
    <Modal title={item.caseNumber} description={`${pct(item.similarity)} similar · ${enumLabel(item.status)}`} onClose={onClose}>
      <dl className="space-y-3">
        <div>
          <dt className={dt}>Incident date</dt>
          <dd className={dd}>{item.incidentDate ? formatCaseDate(item.incidentDate) : 'Not recorded'}</dd>
        </div>
        <div>
          <dt className={dt}>Location</dt>
          <dd className={dd}>{[item.place, item.country].filter(Boolean).join(', ')}</dd>
        </div>
        <div>
          <dt className={dt}>Violation types</dt>
          <dd className="mt-1 flex flex-wrap gap-1">
            {coded.length === 0 && other.length === 0 && <span className="text-sm text-gray-500 dark:text-gray-400">None recorded</span>}
            {coded.map((t) => (
              <span key={t} className={`px-1.5 py-0.5 text-[11px] rounded-full border ${getViolationColor(t).soft}`}>{getViolationTypeLabel(t)}</span>
            ))}
            {other.map((t) => (
              <span key={`o-${t}`} className="px-1.5 py-0.5 text-[11px] rounded-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">{enumLabel(t)}</span>
            ))}
          </dd>
        </div>
        <div>
          <dt className={dt}>Indigenous group{item.indigenousGroups.length === 1 ? '' : 's'} involved</dt>
          <dd className={dd}>{item.indigenousGroups.length > 0 ? item.indigenousGroups.join(', ') : 'Not recorded'}</dd>
        </div>
        <div>
          <dt className={dt}>Description</dt>
          <dd className={`${dd} max-h-56 overflow-y-auto whitespace-pre-wrap leading-5`}>{item.description || 'No description.'}</dd>
        </div>
      </dl>
      <div className="mt-4 flex justify-end">
        <Link to={`/cases/${item.id}`} className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 dark:text-blue-300 hover:underline">
          Open case
          <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </Modal>
  );
};

export default SimilarCasesList;
